import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
    Building2,
    Check,
    ChevronDown,
    Copy,
    Eye,
    EyeOff,
    Loader2,
    UserPlus,
    X,
} from "lucide-react";
import ActionButton from "../ActionButton";
import { INPUT, LABEL, MemberFormData, UserFormData } from "./types";
import { API_BASE_URL } from "../../config/api";

// ─── Modal ────────────────────────────────────────────────────────────────────

export const Modal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    wide?: boolean;
    size?: "sm" | "md" | "lg" | "xl";
}> = ({ isOpen, onClose, title, children, wide = false, size }) => {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) {
            window.addEventListener("keydown", handler);
            document.body.style.overflow = "hidden";
        }
        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // ── Determine modal width ──
    const sizeClass =
        size === "xl"
            ? "max-w-6xl"
            : size === "lg"
            ? "max-w-5xl"
            : size === "sm"
            ? "max-w-sm"
            : wide
            ? "max-w-5xl" // 👈 wider than before
            : "max-w-md";

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={onClose}
        >
            <div
                className={`relative w-full ${sizeClass} bg-[#111111] border border-[#2d2d2d] rounded-lg max-h-[90vh] flex flex-col`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#2d2d2d] flex-shrink-0">
                    <h2 className="text-sm font-semibold text-white">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-500 hover:text-gray-200 rounded transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 p-5">{children}</div>
            </div>
        </div>
    );
};

// ─── Actions Dropdown ─────────────────────────────────────────────────────────

export const ActionsDropdown: React.FC<{
    onCreateMember: () => void;
    onCreateUser: () => void;
}> = ({ onCreateMember, onCreateUser }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                           bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300
                           hover:bg-[#222222] hover:text-white rounded transition-colors"
            >
                Actions
                <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {open && (
                <div className="absolute right-0 mt-1 w-48 bg-[#111111] border border-[#2d2d2d] rounded shadow-xl z-50">
                    <button
                        onClick={() => {
                            onCreateMember();
                            setOpen(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm
                                   text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors"
                    >
                        <Building2 className="w-3.5 h-3.5 text-gray-500" />
                        Create Member
                    </button>
                    <button
                        onClick={() => {
                            onCreateUser();
                            setOpen(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm
                                   text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors
                                   border-t border-[#1f1f1f]"
                    >
                        <UserPlus className="w-3.5 h-3.5 text-gray-500" />
                        Create User
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Create User Form ─────────────────────────────────────────────────────────

export const CreateUserForm: React.FC<{ onClose: () => void }> = ({
    onClose,
}) => {
    const [form, setForm] = useState<UserFormData>({
        email: "",
        password: "",
        memberUuid: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [members, setMembers] = useState<
        { memberUuid: string; companyName: string }[]
    >([]);
    const [membersLoading, setMembersLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/member`)
            .then((r) => r.json())
            .then((data) => setMembers(data))
            .catch(() => {})
            .finally(() => setMembersLoading(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error((await res.text()) || "Failed.");
            toast.success("User created.");
            onClose();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Email */}
                <div>
                    <label className={LABEL}>Email *</label>
                    <input
                        type="email"
                        className={INPUT}
                        value={form.email}
                        onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                        }
                        required
                    />
                </div>

                {/* Password */}
                <div>
                    <label className={LABEL}>Password *</label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            className={`${INPUT} pr-9`}
                            value={form.password}
                            onChange={(e) =>
                                setForm({ ...form, password: e.target.value })
                            }
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2
                                       text-gray-500 hover:text-gray-300 transition-colors"
                            tabIndex={-1}
                        >
                            {showPassword ? (
                                <EyeOff className="w-3.5 h-3.5" />
                            ) : (
                                <Eye className="w-3.5 h-3.5" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Member select */}
                <div>
                    <label className={LABEL}>Member *</label>
                    <select
                        className={INPUT}
                        value={form.memberUuid}
                        onChange={(e) =>
                            setForm({ ...form, memberUuid: e.target.value })
                        }
                        required
                        disabled={membersLoading}
                    >
                        <option value="">
                            {membersLoading ? "Loading…" : "Select a member"}
                        </option>
                        {members.map((m) => (
                            <option key={m.memberUuid} value={m.memberUuid}>
                                {m.companyName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
                <ActionButton
                    onClick={onClose}
                    label="Cancel"
                    variant="ghost"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                               bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300
                               hover:bg-[#222222] hover:text-white rounded transition-colors"
                >
                    {submitting && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    )}
                    {submitting ? "Creating…" : "Create User"}
                </button>
            </div>
        </form>
    );
};

// ─── Create Member Form ───────────────────────────────────────────────────────

export const CreateMemberForm: React.FC<{ onClose: () => void }> = ({
    onClose,
}) => {
    const defaultForm: MemberFormData = {
        companyName: "",
        primaryContactName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",
        contractStartDate: new Date().toISOString().split("T")[0],
        status: "Live",
        securedstichRevenueShare: "", // empty by default
        clientRevenueShare: "",       // empty by default
        shopifyStoreURL: "",
    };

    const [form, setForm] = useState<MemberFormData>(defaultForm);
    const [submitting, setSubmitting] = useState(false);
    const [uuid, setUuid] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [revenueWarning, setRevenueWarning] = useState<string | null>(null);

    // ── Revenue handler ──
    const setRevenue = (
        field: "securedstichRevenueShare" | "clientRevenueShare",
        raw: string
    ) => {
        // Allow only digits (or empty)
        if (raw !== "" && !/^\d+$/.test(raw)) {
            setRevenueWarning("Only numbers are allowed in revenue fields.");
            return;
        }

        const val = raw === "" ? "" : Number(raw);

        // Check if value exceeds 100
        if (typeof val === "number" && val > 100) {
            setRevenueWarning(
                `${
                    field === "securedstichRevenueShare"
                        ? "SS Revenue"
                        : "Client Revenue"
                } cannot exceed 100%.`
            );
            return;
        }

        // Clear warning on valid input
        setRevenueWarning(null);

        if (field === "securedstichRevenueShare") {
            setForm((f) => ({
                ...f,
                securedstichRevenueShare: val,
                clientRevenueShare: typeof val === "number" ? 100 - val : "",
            }));
        } else {
            setForm((f) => ({
                ...f,
                clientRevenueShare: val,
                securedstichRevenueShare:
                    typeof val === "number" ? 100 - val : "",
            }));
        }
    };

    // ── Validate before submit ──
    const validateForm = (): string | null => {
        const ss = form.securedstichRevenueShare;
        const client = form.clientRevenueShare;

        if (ss === "" || client === "") {
            return "Both SS Revenue % and Client Revenue % are required.";
        }

        if (typeof ss === "number" && typeof client === "number") {
            if (ss + client !== 100) {
                return `Revenue split must total 100%. Currently it's ${
                    ss + client
                }%.`;
            }
        }

        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate revenue
    const validationError = validateForm();
    if (validationError) {
        toast.error(validationError);
        return;
    }

    setSubmitting(true);
    const payload: any = { ...form };
    if (!payload.shopifyStoreURL?.trim()) delete payload.shopifyStoreURL;

    try {
        const res = await fetch(`${API_BASE_URL}/api/member`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        // ── Handle error responses with clear messages ──
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));

            let errorMessage = "Failed to create member.";

            // Duplicate entry (409)
            if (res.status === 409 && err.field) {
                const fieldLabelMap: Record<string, string> = {
                    shopifyStoreURL: "Shopify Store URL",
                    primaryContactEmail: "Contact Email",
                    primaryContactPhone: "Contact Phone",
                    companyName: "Company Name",
                };
                const readableField =
                    fieldLabelMap[err.field] || err.field;
                errorMessage = `A member with this ${readableField} already exists. Please use a different one.`;
            }
            // Validation error (400) — phone/date/etc.
            else if (res.status === 400) {
                errorMessage =
                    err.message ||
                    err.error ||
                    "Invalid input. Please check your data.";
            }
            // Fallback for any other error
            else {
                errorMessage =
                    err.message ||
                    err.error ||
                    `Request failed (${res.status})`;
            }

            throw new Error(errorMessage);
        }

        const data = await res.json();
        setUuid(data.memberUuid);
        toast.success("Member created successfully.");
    } catch (err: any) {
        toast.error(err.message, { duration: 5000 });
    } finally {
        setSubmitting(false);
    }
};

    const copyUuid = async () => {
        if (!uuid) return;
        await navigator.clipboard.writeText(uuid);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const reset = () => {
        setUuid(null);
        setForm(defaultForm);
        setRevenueWarning(null);
    };

    // ── Revenue total for display ──
    const ssVal =
        typeof form.securedstichRevenueShare === "number"
            ? form.securedstichRevenueShare
            : 0;
    const clientVal =
        typeof form.clientRevenueShare === "number"
            ? form.clientRevenueShare
            : 0;
    const revenueTotal = ssVal + clientVal;
    const bothFilled =
        form.securedstichRevenueShare !== "" &&
        form.clientRevenueShare !== "";

    // ── Success screen ──
    if (uuid) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-white">
                        Member created successfully
                    </span>
                </div>
                <div>
                    <label className={LABEL}>
                        Member UUID — save this before closing
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm font-mono text-green-400 break-all">
                            {uuid}
                        </code>
                        <button
                            onClick={copyUuid}
                            className="flex-shrink-0 p-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-gray-400 hover:text-white transition-colors"
                        >
                            {copied ? (
                                <Check className="w-4 h-4 text-green-500" />
                            ) : (
                                <Copy className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                </div>
                <div className="flex gap-2 pt-1">
                    <ActionButton
                        onClick={reset}
                        label="Create Another"
                        variant="ghost"
                    />
                    <ActionButton onClick={onClose} label="Done" />
                </div>
            </div>
        );
    }

    // ── Form ──
    const contactFields = [
        "companyName",
        "primaryContactName",
        "primaryContactEmail",
        "primaryContactPhone",
    ] as const;

    const fieldLabels: Record<string, string> = {
        companyName: "Company Name",
        primaryContactName: "Contact Name",
        primaryContactEmail: "Contact Email",
        primaryContactPhone: "Contact Phone",
    };

    const fieldTypes: Record<string, string> = {
        primaryContactEmail: "email",
        primaryContactPhone: "tel",
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1 — Contact fields (2 columns for more room) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contactFields.map((field) => (
                    <div key={field}>
                        <label className={LABEL}>{fieldLabels[field]} *</label>
                        <input
                            type={fieldTypes[field] || "text"}
                            className={INPUT}
                            value={form[field]}
                            onChange={(e) =>
                                setForm({ ...form, [field]: e.target.value })
                            }
                            required
                        />
                    </div>
                ))}
            </div>

            {/* Row 2 — Contract / status / revenue (4 columns) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                    <label className={LABEL}>Contract Start *</label>
                    <input
                        type="date"
                        className={INPUT}
                        value={form.contractStartDate}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                contractStartDate: e.target.value,
                            })
                        }
                        required
                    />
                </div>
                <div>
                    <label className={LABEL}>Status *</label>
                    <select
                        className={INPUT}
                        value={form.status}
                        onChange={(e) =>
                            setForm({ ...form, status: e.target.value })
                        }
                        required
                    >
                        <option value="Live">Live</option>
                        <option value="Pending">Pending</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <div>
                    <label className={LABEL}>SS Revenue % *</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 65"
                        className={`${INPUT} ${
                            revenueWarning ? "border-red-500/50" : ""
                        }`}
                        value={form.securedstichRevenueShare}
                        onChange={(e) =>
                            setRevenue(
                                "securedstichRevenueShare",
                                e.target.value
                            )
                        }
                        required
                    />
                </div>
                <div>
                    <label className={LABEL}>Client Revenue % *</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 35"
                        className={`${INPUT} ${
                            revenueWarning ? "border-red-500/50" : ""
                        }`}
                        value={form.clientRevenueShare}
                        onChange={(e) =>
                            setRevenue("clientRevenueShare", e.target.value)
                        }
                        required
                    />
                </div>
            </div>

            {/* Row 3 — Shopify URL (full-width) */}
            <div>
                <label className={LABEL}>Shopify URL</label>
                <input
                    type="text"
                    placeholder="example.myshopify.com"
                    className={INPUT}
                    value={form.shopifyStoreURL}
                    onChange={(e) =>
                        setForm({
                            ...form,
                            shopifyStoreURL: e.target.value,
                        })
                    }
                />
            </div>

            {/* Revenue warning */}
            {revenueWarning && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded">
                    <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    <span className="text-xs text-red-400">
                        {revenueWarning}
                    </span>
                </div>
            )}

            {/* Revenue split visualiser */}
            <div className="border border-[#2d2d2d] rounded p-3 space-y-1.5">
                <div className="flex justify-between text-xs text-gray-500">
                    <span>Revenue Split</span>
                    <span
                        className={
                            bothFilled && revenueTotal !== 100
                                ? "text-red-500"
                                : bothFilled && revenueTotal === 100
                                ? "text-green-500"
                                : ""
                        }
                    >
                        {bothFilled
                            ? `${revenueTotal}% total`
                            : "— enter values —"}
                    </span>
                </div>
                <div className="h-1.5 bg-[#2d2d2d] rounded overflow-hidden flex">
                    <div
                        className="bg-blue-600 transition-all duration-300"
                        style={{ width: `${ssVal}%` }}
                    />
                    <div
                        className="bg-gray-400 transition-all duration-300"
                        style={{ width: `${clientVal}%` }}
                    />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                    <span>
                        SS:{" "}
                        {form.securedstichRevenueShare === ""
                            ? "—"
                            : `${form.securedstichRevenueShare}%`}
                    </span>
                    <span>
                        Client:{" "}
                        {form.clientRevenueShare === ""
                            ? "—"
                            : `${form.clientRevenueShare}%`}
                    </span>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
                <ActionButton
                    onClick={onClose}
                    label="Cancel"
                    variant="ghost"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                               bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300
                               hover:bg-[#222222] hover:text-white rounded transition-colors"
                >
                    {submitting && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    )}
                    {submitting ? "Creating…" : "Create Member"}
                </button>
            </div>
        </form>
    );
};